// src/utils/portalFeatures.js
// Client Portal feature switches. Henry 10/06/2026: "We should also be able to turn on and off features in the
// client portal as well." Two levels, and he chose both:
//   1. Team-wide: Settings > Client Portal, one On/Off switch per feature (appSettings/clientPortal
//      { features: { timeline: false, ... } }). Team Lead or Henry change it.
//   2. Per client: the deal page's Client Portal panel, saved on the deal as
//      portalFeatureOverrides { openHouses: "off", market: "on", ... }. Absent means "use the team default".
// Unknown is never off: only a deliberate false (team) or "off" (client) turns a feature off, so a missing doc,
// a failed read or an older server leaves the portal exactly as it was. Home itself can never be turned off,
// and the day-before reminders are not one of these switches.
//
// The server (functions-api/portalFeatures.js, same ids and the same resolve rule) is the authority: it blanks an
// off feature's data before the payload leaves. This file lets the page hide the tab or card, and lets the
// Settings tab and the deal panel show and save the switches. Pure: no React, no Firestore.

export const PORTAL_FEATURES = [
  { id: "timeline", label: "Timeline", hint: "The Timeline tab and the dates on Home." },
  { id: "openHouses", label: "Open Houses", hint: "The Open Houses tab and open house requests. Sellers only." },
  { id: "forms", label: "Forms", hint: "The Forms tab and the form reminders on Home." },
  { id: "guide", label: "Guide", hint: "The Guide tab and its card on Home." },
  { id: "market", label: "Competition", hint: "Active homes near the seller's list price. Sellers only." },
  { id: "netSheet", label: "Net Sheet", hint: "The Estimate of Proceeds tab. Sellers only." },
  { id: "moneyOwed", label: "Money Owed", hint: "Costs the client owes the agent, while anything is owed." },
  { id: "transactionTeam", label: "Your Real Estate Team", hint: "The card on Home with each person's contact details." },
];

export const PORTAL_FEATURE_IDS = PORTAL_FEATURES.map((f) => f.id);

export const PORTAL_FEATURES_DOC = ["appSettings", "clientPortal"];

// Which feature owns each portal tab (src/utils/portalNav.js ids). Home has none: it is always there.
export const TAB_FEATURE = {
  timeline: "timeline",
  openhouses: "openHouses",
  forms: "forms",
  guide: "guide",
  market: "market",
  netsheet: "netSheet",
  money: "moneyOwed",
};

const isObj = (v) => !!v && typeof v === "object" && !Array.isArray(v);

export const ALL_FEATURES_ON = Object.freeze(Object.fromEntries(PORTAL_FEATURE_IDS.map((id) => [id, true])));

// The team switches from appSettings/clientPortal, every id present: true unless the doc deliberately says false.
export function cleanTeamFeatures(teamDoc) {
  const raw = isObj(teamDoc) && isObj(teamDoc.features) ? teamDoc.features : {};
  return Object.fromEntries(PORTAL_FEATURE_IDS.map((id) => [id, raw[id] !== false]));
}

// One client's overrides: only "on" / "off" for known ids survive. Anything else means "team default".
export function cleanFeatureOverrides(raw) {
  const src = isObj(raw) ? raw : {};
  const out = {};
  PORTAL_FEATURE_IDS.forEach((id) => {
    if (src[id] === "on" || src[id] === "off") out[id] = src[id];
  });
  return out;
}

// -> { timeline: true, openHouses: false, ... }: the client's override wins, else the team default.
export function resolvePortalFeatures(teamDoc, overrides) {
  const team = cleanTeamFeatures(teamDoc);
  const own = cleanFeatureOverrides(overrides);
  return Object.fromEntries(
    PORTAL_FEATURE_IDS.map((id) => [id, own[id] === "on" ? true : own[id] === "off" ? false : team[id]]),
  );
}

// The `features` a portal payload carries. Missing (an older server) or junk reads as everything on.
export function pickPortalFeatures(raw) {
  const src = isObj(raw) ? raw : {};
  return Object.fromEntries(PORTAL_FEATURE_IDS.map((id) => [id, src[id] !== false]));
}

// Is the tab with this portal tab id switched on? Home, and any tab no feature owns, always is.
export function tabFeatureOn(features, tabId) {
  const id = TAB_FEATURE[tabId];
  if (!id) return true;
  return !(isObj(features) && features[id] === false);
}

// A Home hero or band model with every link and button into a turned-off tab removed, so Home never offers a
// way into something the client cannot open.
export function withoutOffTabLinks(model, features) {
  if (!model || typeof model !== "object") return model;
  const on = (l) => !l || !l.to || tabFeatureOn(features, l.to.formId ? "forms" : l.to.tab);
  const out = { ...model };
  if (out.link && !on(out.link)) out.link = null;
  if (out.primary && !on(out.primary)) out.primary = null;
  return out;
}
